import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchLeadsApi,
  filterLeadsApi,
  addOfferApi,
  deleteLeadsApi,
  updateLeadStatusApi,
  postMultipleLeadsApi,
  updateLeadApi,
} from "./leadsAPI";

// Fetch Leads with Pagination and Sorting
export const fetchLeads = createAsyncThunk(
  "leads/fetchLeads",
  async (
    {
      coachId,
      pageNumber,
      pageSize,
      sortOrder,
      statusFilter,
      sortBy = "CreatedAt",
    },
    { rejectWithValue }
  ) => {
    try {
      return await fetchLeadsApi({
        coachId,
        pageNumber,
        pageSize,
        sortOrder,
        status: statusFilter || "",
        sortBy,
      });
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch leads");
    }
  }
);

// Search and Filter Leads
export const filterLeads = createAsyncThunk(
  "leads/filterLeads",
  async ({ coachId, name, status, sortOrder }, { rejectWithValue }) => {
    try {
      return await filterLeadsApi({ coachId, name, status, sortOrder });
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to filter leads");
    }
  }
);

export const postMultipleLeadsThunk = createAsyncThunk(
  "leads/postMultipleLeads",
  async ({ coachId, leads }, { rejectWithValue }) => {
    try {
      return await postMultipleLeadsApi(coachId, leads);
    } catch (error) {
      return rejectWithValue(error?.data || "Failed to post leads");
    }
  }
);

// Update Lead Thunk
export const updateLeadThunk = createAsyncThunk(
  "leads/updateLead",
  async ({ leadId, ...leadData }, { rejectWithValue }) => {
    try {
      return await updateLeadApi(leadId, leadData);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update lead");
    }
  }
);

// Delete Leads
export const deleteLeads = createAsyncThunk(
  "leads/deleteLeads",
  async (leadIds, { rejectWithValue }) => {
    try {
      return await deleteLeadsApi(leadIds);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete leads");
    }
  }
);

export const updateLeadStatus = createAsyncThunk(
  "leads/updateLeadStatus",
  async ({ coachId, leadId, status }, { rejectWithValue }) => {
    try {
      return await updateLeadStatusApi(coachId, leadId, status);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to update lead status"
      );
    }
  }
);

// Add Offer
export const addOffer = createAsyncThunk(
  "leads/addOffer",
  async (offerData, { rejectWithValue }) => {
    try {
      return await addOfferApi(offerData);
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to add offer");
    }
  }
);

// Slice
const leadsSlice = createSlice({
  name: "leads",
  initialState: {
    leads: [],
    totalCount: 0,
    loading: false,
    error: null,
    pagination: { current: 1, pageSize: 10 },
    sortOrder: "desc",
    searchQuery: "",
    statusFilter: "All",
    toastConfig: null,
  },
  reducers: {
    updatePagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };

      if (action.payload.current === 1) {
        state.pagination.current = 1;
      }
    },

    updateSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
    updateSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    updateStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
    },
    setToastForAddLead: (state, action) => {
      state.toastConfig = action.payload;
    },
    resetToastConfig: (state) => {
      state.toastConfig = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload.leads.map((lead) => ({
          key: lead.leadId,
          name: `${lead.firstName} ${lead.lastName}`,
          phone: lead.phoneNumber,
          email: lead.email,
          language: lead.language,
          country: lead.country,
          status: lead.status,
          isLeadFromSite: lead.isLeadFromSite,
          createdOn: lead.createdOn,
          updatedOn: lead.updatedOn,
          note: lead.note,
          age: lead.age,
          sex: lead.sex,
          goal: lead.goal,
          motivation: lead.motivation,
          sixMonthFitnessGoal: lead.sixMonthFitnessGoal,
          isPreparedToInvest: lead.isPreparedToInvest,
          timeZone: lead.timeZone,
        }));
        state.totalCount = action.payload.totalCount;
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(filterLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(filterLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload.leads.map((lead) => ({
          key: lead.leadId,
          name: `${lead.firstName} ${lead.lastName}`,
          phone: lead.phoneNumber,
          email: lead.email,
          language: lead.language,
          country: lead.country,
          status: lead.status,
          note: lead.note,
          isLeadFromSite: lead.isLeadFromSite,
          createdOn: lead.createdOn,
          updatedAt: lead.updatedAt,
          age: lead.age,
          sex: lead.sex,
          goal: lead.goal,
          motivation: lead.motivation,
          sixMonthFitnessGoal: lead.sixMonthFitnessGoal,
          isPreparedToInvest: lead.isPreparedToInvest,
          timeZone: lead.timeZone,
        }));

        state.totalCount = action.payload.totalCount;

        if (action.meta.arg.pageNumber) {
          state.pagination = {
            ...state.pagination,
            current: action.meta.arg.pageNumber,
          };
        }
      })
      .addCase(postMultipleLeadsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(postMultipleLeadsThunk.fulfilled, (state) => {
        state.loading = false;
        state.toastConfig = {
          type: "success",
          message: "Leads created successfully!",
          heading: "Success",
        };
      })
      .addCase(postMultipleLeadsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        const errorMessage =
          action?.payload?.message ||
          "Failed to create leads. Please try again.";

        state.toastConfig = {
          type: "error",
          message: errorMessage,
          heading: "Error",
        };
      })

      .addCase(updateLeadThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLeadThunk.fulfilled, (state) => {
        state.loading = false;
        state.toastConfig = {
          type: "success",
          message: "Lead updated successfully!",
          heading: "Success",
        };
      })
      .addCase(updateLeadThunk.rejected, (state, action) => {
        state.loading = false;
        state.toastConfig = {
          type: "error",
          message: action.payload || "Failed to update lead.",
          heading: "Error",
        };
      })

      .addCase(deleteLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteLeads.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(deleteLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateLeadStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLeadStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.toastConfig = {
          type: "success",
          message: action.payload.message,
          heading: "Success",
        };
      })
      .addCase(updateLeadStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  updatePagination,
  updateSortOrder,
  updateSearchQuery,
  updateStatusFilter,
  setToastForAddLead,
  resetToastConfig,
} = leadsSlice.actions;

export default leadsSlice.reducer;
